import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatPhoneByCode } from './phone';
import { parseInertHtml } from './inertHtml';

/**
 * Merges Tailwind CSS class names safely, resolving conflicts via tailwind-merge.
 * This is the canonical utility for composing class names in components.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Format a date string or Date to a US-readable date */
export function parseDateValue(date: string | Date | null | undefined): Date | null {
  if (!date) return null;
  if (typeof date === 'string') {
    const trimmed = date.trim();
    if (!trimmed || trimmed.startsWith('0000-00-00')) return null;
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? `${trimmed}T00:00:00` : trimmed;
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a Date as a bare 'YYYY-MM-DD' calendar date in the browser's LOCAL zone.
 *
 * Never use `toISOString().slice(0, 10)` for this. That returns the UTC calendar
 * date, so in any negative-offset zone the late-evening hours already read as
 * tomorrow — e.g. 5pm Pacific is midnight UTC, which locked the membership
 * start-date picker's `min` to the following day.
 */
export function toYmd(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today's calendar date in the browser's local zone as 'YYYY-MM-DD'. */
export function todayYmd(): string {
  return toYmd(new Date());
}

/** Format a date string or Date to a US-readable date */
export function formatDate(date: string | Date | null | undefined): string {
  const d = parseDateValue(date);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Format a date string or Date to a datetime string */
export function formatDateTime(date: string | Date | null | undefined): string {
  const d = parseDateValue(date);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a phone number with its dial code: +1 (XXX) XXX-XXXX for code '1',
 * libphonenumber's international format for any other (see formatPhoneByCode).
 */
export function formatPhone(phone: string | null | undefined, countryCode: string = '1'): string {
  if (!phone) return '—';
  return formatPhoneByCode(phone, countryCode) || phone;
}

/**
 * Parse a currency value that may be a comma-formatted string (e.g. "10,000.00")
 * into a clean number. Returns 0 for null/undefined/NaN.
 */
export function parseCurrency(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;
  const cleaned = String(value).replace(/,/g, '');
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

/** Format a number as USD currency */
export function formatCurrency(
  amount: number | null | undefined,
  opts?: { cents?: boolean },
): string {
  if (amount == null) return '—';
  const value = opts?.cents ? amount / 100 : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a whole-number count with thousands separators (1000 → "1,000").
 * Pinned to en-US so a browser in another locale doesn't render "1.000".
 */
export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** Get initials from a display name or first/last */
export function getInitials(name: string | null | undefined, max = 2): string {
  if (!name?.trim()) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, max)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/** Truncate a string with an ellipsis */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '…';
}

/**
 * Legacy web-lead ingestion stores `contacts.message` as `{preview}{DELIM}{rawHtmlEmail}`.
 * The preview is a short human-readable intro; the raw half is the original email HTML,
 * which is typically rendered in an isolated iframe.
 *
 * Mirror of `LeadsController::RAW_CONTENT_DELIM` and `splitRawContent()` in API_v5.
 */
export const RAW_CONTENT_DELIM = '!*******BEGIN RAW CONTENT*******!';

/**
 * Split a message field on the legacy RAW CONTENT delimiter.
 * Subsequent delimiters (rare) are preserved inside the raw body — matches legacy
 * behavior in `lead_display_content.php`.
 */
export function splitRawContent(
  text: string | null | undefined,
): { preview: string; raw: string | null; hasRaw: boolean } {
  if (!text) return { preview: '', raw: null, hasRaw: false };
  const idx = text.indexOf(RAW_CONTENT_DELIM);
  if (idx === -1) return { preview: text, raw: null, hasRaw: false };
  const preview = text.slice(0, idx);
  const raw = text.slice(idx + RAW_CONTENT_DELIM.length);
  return { preview, raw, hasRaw: true };
}

/** Convert HTML content into readable plain text for previews/tooltips. */
export function stripHtmlToText(value: string | null | undefined): string {
  if (!value) return '';
  let decoded = value;
  for (let i = 0; i < 3; i += 1) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    const nextValue = textarea.value;
    if (!nextValue || nextValue === decoded) {
      break;
    }
    decoded = nextValue;
  }
  const tmp = parseInertHtml(decoded);
  tmp.querySelectorAll('script, style, noscript, iframe, object, embed, template').forEach((node) => {
    node.remove();
  });
  return (tmp.textContent ?? tmp.innerText ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * A membership, package, product, product type or appointment type description
 * as one line of text, for list cells, tooltips and search. Descriptions are
 * rich text (HTML) since the editor; older ones are plain text and come back
 * unchanged. A line break and the end of a block (paragraph, list item,
 * heading, quote…) become a space, so "<p>One</p><p>Two</p>" reads "One Two".
 */
export function descriptionToText(value: string | null | undefined): string {
  const v = value ?? '';
  if (!/<\/?[a-z][^>]*>/i.test(v)) return v;
  return stripHtmlToText(v.replace(/<(?:br|hr)\b[^>]*>|<\/(?:p|div|li|h[1-6]|blockquote|ul|ol|tr|td|th)\s*>/gi, '$& '));
}

/**
 * Compute age from a date of birth string.
 *
 * Parses via `parseDateValue` so a bare 'YYYY-MM-DD' DOB is read as local
 * midnight. A bare `new Date(dob)` would land on UTC midnight, which in a
 * negative-offset zone reads back as the previous calendar day and ticked the
 * age over one day early.
 */
export function getAge(dob: string | null | undefined): number | null {
  const birth = parseDateValue(dob);
  if (!birth) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

/** Generate a random hex color for avatar backgrounds */
const AVATAR_COLORS = [
  'bg-blue-600',
  'bg-indigo-600',
  'bg-violet-600',
  'bg-slate-600',
  'bg-teal-600',
  'bg-cyan-600',
  'bg-emerald-600',
  'bg-sky-600',
];

/**
 * Convert a hex color (#rrggbb or rrggbb) to an HSL triplet string
 * suitable for Tailwind CSS variables: "h s% l%"
 */
export function hexToHslString(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return `0 0% ${Math.round(l * 100)}%`;
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let hue = 0;
  if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) hue = ((b - r) / d + 2) / 6;
  else hue = ((r - g) / d + 4) / 6;

  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Apply account branding button color to the global CSS variables.
 * Sets --primary (HSL for shadcn/Tailwind) and --color-primary (hex for legacy).
 */
export function applyBrandingColors(buttonColor: string | null | undefined) {
  if (!buttonColor) return;
  const hex = buttonColor.startsWith('#') ? buttonColor : `#${buttonColor}`;
  const hsl = hexToHslString(hex);
  document.documentElement.style.setProperty('--primary', hsl);
  document.documentElement.style.setProperty('--color-primary', hex);
}

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
